import { takeUntil } from 'rxjs/operators';
import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  Input,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { AuthService, UserData, SYSTEMCATEGORY } from './../../../services/auth/auth.service';
import { SharedService } from '../../../services/shared/shared.service';
import { StorageService } from '../../../services/storage/storage.service';
import { NotifyService } from '../notify/notify.service';
import { RoutingService } from '../../../services/routing/routing.service';
import { Subject } from 'rxjs';
import { LayoutService } from '../../../services/layout.service';
import { SidenavContent } from '../sidenav-content';
import { RequestService } from '../../../pages/request/request.service';
import { Router } from '@angular/router';
import { LayoutSettings, Page } from '../../../services/edit/predefined-templates.service';
import { AnalyticsService } from '../../../services/analytics/analytics.service';

@Component({
  selector: 'ag-profile-nav',
  templateUrl: './profile-nav.component.html',
  styleUrls: ['./profile-nav.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ProfileNavComponent extends SidenavContent implements OnInit, OnDestroy {
  @Input() pages: Page[];
  @Input() a11y: boolean;
  @Output() resetA11y = new EventEmitter<any>();
  @Output() toggleSideNav = new EventEmitter<any>();
  @Output() openLibraryCard = new EventEmitter<any>();

  SYSTEMCATEGORY = SYSTEMCATEGORY;
  userData: UserData;
  showList: boolean;
  showDigitalLibraryCard: boolean;
  showGuestTracking: boolean;
  layoutSettings: LayoutSettings;

  private _unsubscribeAll: Subject<void> = new Subject<void>();

  constructor(
    private _router: Router,
    private _authService: AuthService,
    private _layoutService: LayoutService,
    private _notifyService: NotifyService,
    private _requestService: RequestService,
    private _routingService: RoutingService,
    public sharedService: SharedService,
    public storageService: StorageService,
    private _analyticsService: AnalyticsService,
  ) {
    super(_layoutService);
  }

  ngOnInit() {
    this.sharedService.userData$.pipe(takeUntil(this._unsubscribeAll)).subscribe((userData) => {
      if (!userData) {
        return;
      }
      this.userData = userData;
      this.showList = this._authService.hasDisplayYourListPermission();
      this.showDigitalLibraryCard =
        this.sharedService.isMobile() &&
        this.userData.systemCategory == SYSTEMCATEGORY.VERSO &&
        this.userData.isPatron &&
        this.userData.showDigitalLibraryCard;
      this.showGuestTracking =
        this.userData?.systemCategory == SYSTEMCATEGORY.SHAREIT &&
        this.userData?.isGuest &&
        this.userData.illOptions.showGuestTracking;
    });
  }

  ngOnDestroy() {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  private _beforeGoTo(): void {
    this.closeNav();
    this._resetA11y();
  }

  private _resetA11y(): void {
    this.resetA11y.emit();
  }

  goToProfile(): void {
    this._beforeGoTo();
    this._routingService.goToAccountProfile();
  }

  goToPreferences(): void {
    this._beforeGoTo();
    this._routingService.goToAccountPreferences();
  }

  goToResources(): void {
    this._beforeGoTo();
    this._routingService.goToAccountResources();
  }

  goToWebLinks(): void {
    this._beforeGoTo();
    this._routingService.goToAccountWebLinks();
  }

  goToDashboard(): void {
    this._beforeGoTo();
    this._routingService.goToDashboard();
  }

  goToItemsOut(): void {
    this._beforeGoTo();
    this._routingService.goToItemsOut();
  }

  goToItemsHold(): void {
    this._beforeGoTo();
    this._routingService.goToItemsHold();
  }

  goToLostItems(): void {
    this._beforeGoTo();
    this._routingService.goToLostItems();
  }

  goToCheckoutHistory(): void {
    this._beforeGoTo();
    this._routingService.goToCheckoutHistory();
  }

  goToSearchAlerts(): void {
    this._beforeGoTo();
    this._routingService.goToSearchAlerts();
  }

  goToFines(): void {
    this._beforeGoTo();
    this._routingService.goToFines();
  }

  goToOtherLibraries(): void {
    this._beforeGoTo();
    this._routingService.goToOtherLibraries();
  }

  goToIllRequestBlank(): void {
    this._beforeGoTo();
    this.goToBlankILLRequest();
  }

  goToBlankILLRequest() {
    if (this._router.url.includes('/request')) {
      return;
    }
    this._requestService.requestThisItemBlank();
  }

  goToLogin(): void {
    this._beforeGoTo();
    this._routingService.goToLogin();
  }

  goToYourList(): void {
    this._beforeGoTo();
    this.closeNav();
    if (this._authService.isGuest()) {
      this._layoutService.openSideNav({
        template: 'addList',
        position: 'end',
        data: true,
      });
    } else {
      this._routingService.goToYourList();
    }
  }

  goToHome(): void {
    this._beforeGoTo();
    this.closeNav();
    if (!this.sharedService.getIsKidsCat()) {
      this._routingService.goToHome();
    } else {
      this._routingService.goToKidsCat();
    }
  }

  openSearchHistory(): void {
    this._resetA11y();
    this._analyticsService.logEvent('View Search History');

    this.toggleSideNav.emit({
      position: 'start',
      template: 'history',
    });
  }

  goToPage(page: Page): void {
    this._beforeGoTo();
    this._routingService.goToPage(page);
  }

  goToAdvancedSearch(): void {
    this._beforeGoTo();
    this._routingService.goToAdvancedSearch();
  }

  openNotifications(): void {
    this._beforeGoTo();
    this._notifyService.setOpenNotSideNavBS(true);
  }

  openCard(): void {
    this._beforeGoTo();
    this.openLibraryCard.emit({
      showLibraryCard: true,
      libraryCardId: '*' + this.userData.userName + '*',
      libraryCardLibraryName: this.userData.libraryName,
    });
  }

  openQuickMenu(): void {
    this._beforeGoTo();
    this.layoutService.openSideNav({ template: 'quickmenu', position: 'end' });
  }

  openGuestTracking(): void {
    this._beforeGoTo();
    this._authService.getIllGuestInstructions().subscribe(
      (response) => {
        this._layoutService.openSideNav({
          template: 'tracking',
          position: 'end',
          data: response,
        });
      },
      (error: unknown) => {
        if (error instanceof Error) {
          console.error(error.message);
        }
        this.sharedService.openSnackBar('Unable to get Ill tracking instructions.');
      },
    );
  }

  onLogout(): void {
    this._beforeGoTo();
    this._authService.OnLogOut();
  }
}
